// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * The college lifecycle, validated against the v2 contract (**ADR-0076**).
 *
 * A **separate module from** `schemas.ts`. That file holds the v1 validators
 * every existing screen imports, and this one is written against
 * `contracts.generated.v2.ts`. Keeping them apart is what lets v2 arrive without
 * touching a single v1 consumer: nothing here is re-exported into the v1
 * surface, and nothing there changes shape.
 *
 * **The contract is the source of truth, and the compiler enforces it.** Each
 * validator below is asserted to produce exactly the generated contract type
 * through {@link Exact}, so a schema that drifted — a renamed field, a widened
 * optional, a forgotten member — is a build failure rather than a runtime
 * surprise on a screen. The generated types cannot themselves validate at
 * runtime, and the validators cannot themselves be trusted to match; pairing
 * them is what makes both true.
 *
 * **What these types cannot carry.** `ActivationSummary` has no `code` and no
 * digest; `OnboardingSummary` has no key, thumbprint, replay identifier, or
 * attempt counter. `strict()` refuses an unexpected member outright rather than
 * stripping it, so a response carrying one fails here — at the last boundary
 * before a component renders it — as well as at the BFF.
 *
 * The one exception is deliberate and narrow: {@link activationIssuanceSchema}
 * carries the plaintext activation code, because two responses disclose it once
 * (**ADR-0076** §3). It is used by create and rotation and by nothing else.
 */

import { z } from 'zod';

import type { components } from './contracts.generated.v2';

/**
 * Compile-time proof that a validator's output is the contract type exactly.
 *
 * Assignability alone is too weak in both directions: a validator missing a
 * field is still assignable *to* a wider type, and one with an extra field is
 * still assignable *from* a narrower one. Mutual extension catches both.
 */
type Exact<TActual, TExpected> = [TActual] extends [TExpected]
  ? [TExpected] extends [TActual]
    ? true
    : never
  : never;

/** Central tenant lifecycle (**ADR-0067**). */
export const tenantStatusV2Schema = z.enum(['pending', 'active', 'deactivated']);

/** Activation-code lifecycle (**ADR-0076** §1). */
export const activationStateSchema = z.enum([
  'not_issued',
  'active',
  'claimed',
  'expired',
  'revoked',
  'not_applicable',
]);

/** How far an appliance has got (**ADR-0076** §1). */
/**
 * How far a college's Edge appliance has got.
 *
 * Its own vocabulary, separate from `onboardingState`. An appliance can be
 * `ready_for_activation` here while its onboarding state is still
 * `activation_required`: the allocation exists and the college has not yet
 * claimed. Both are true, about different things.
 */
export const edgeProvisioningStateSchema = z.enum([
  'requested',
  'provisioning',
  'ready_for_activation',
  'failed',
  'decommission_requested',
  'decommissioning',
  'decommissioned',
]);

/** Why an allocation failed, as a closed set the server publishes. */
export const edgeProvisioningFailureSchema = z.enum([
  'refused_by_edge',
  'edge_unreachable',
  'operation_failed',
  'timed_out',
  'attempts_exhausted',
]);

/**
 * One college's Edge allocation, as the administrator's screen sees it.
 *
 * Nullable at the call site: a college nobody has approved has no allocation,
 * and the server returns `null` rather than inventing a record.
 */
export const edgeProvisioningViewSchema = z
  .object({
    tenant_id: z.string().min(1),
    state: edgeProvisioningStateSchema,
    request_id: z.string().min(1),
    operation_id: z.string().nullable().default(null),
    failure_reason: edgeProvisioningFailureSchema.nullable().default(null),
    attempts: z.number().int().nonnegative(),
    can_retry: z.boolean(),
    setup_is_open: z.boolean(),
    setup_url: z.string().nullable().default(null),
    version: z.number().int().positive(),
    updated_at: z.string().min(1),
  })
  .strict();

export type EdgeProvisioningView = z.infer<typeof edgeProvisioningViewSchema>;
export type EdgeProvisioningState = z.infer<typeof edgeProvisioningStateSchema>;

export const onboardingStateSchema = z.enum([
  'activation_required',
  'configuration_required',
  'verifying',
  'failed',
  'ready',
  'deactivated',
]);

/**
 * Non-secret activation state.
 *
 * `last_four` is the only part of a code that ever reaches a screen. It exists
 * so an operator can tell which code a row holds; it is four characters of a
 * 140-bit value and identifies without disclosing.
 */
export const activationSummarySchema = z
  .object({
    state: activationStateSchema,
    last_four: z.string().nullable().default(null),
    expires_at: z.string().nullable().default(null),
    claimed_at: z.string().nullable().default(null),
  })
  .strict();

/** How far a college's appliance has got. */
export const onboardingSummarySchema = z
  .object({
    state: onboardingStateSchema,
    requested_generation: z.number().int(),
    observed_generation: z.number().int().nullable().default(null),
    safe_reason: z.string().nullable().default(null),
  })
  .strict();

/**
 * One college row: registration, activation, and onboarding together.
 *
 * `onboarding` is nullable because a college registered before activation
 * existed has no onboarding row at all — `SVRC` is exactly that. The row is
 * still rendered; a screen that dropped it would hide the college most in need
 * of a code.
 */
export const tenantOverviewSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    code: z.string(),
    address: z.string().default(''),
    status: tenantStatusV2Schema,
    registered_at: z.string(),
    token_issued_at: z.string().nullable().default(null),
    token_tail: z.string().nullable().default(null),
    activation: activationSummarySchema,
    onboarding: onboardingSummarySchema.nullable().default(null),
  })
  .strict();

/** The page envelope the college list returns. */
export const tenantOverviewPageSchema = z
  .object({
    items: z.array(tenantOverviewSchema),
    // All six fields Platform publishes. `has_next` and `has_previous` are not
    // read by any screen, but `.strict()` means declaring four is not
    // "ignoring two" — it is refusing the response outright, which is exactly
    // what happened: the BFF's matching four-field model answered `503` for
    // every real page and the Tenants screen never rendered.
    //
    // The rows are checked against the generated contract by `Exact<>` below.
    // This envelope is not: `TenantOverviewPage` has no entry in the v2
    // aggregate, so it is hand-written here and in the BFF, and agreement
    // between them is the only thing holding it together.
    pagination: z
      .object({
        page: z.number().int(),
        page_size: z.number().int(),
        total_items: z.number().int(),
        total_pages: z.number().int(),
        has_next: z.boolean().default(false),
        has_previous: z.boolean().default(false),
      })
      .strict(),
  })
  .strict();

/**
 * The one-time disclosure of an activation code (**ADR-0076** §3).
 *
 * The only validator in this module that carries a plaintext credential, and the
 * only one two operations may return. It exists because a code has to reach the
 * operator exactly once; every other shape here is deliberately incapable of
 * carrying it.
 */
export const activationIssuanceSchema = z
  .object({
    tenant_id: z.string(),
    code: z.string(),
    expires_at: z.string(),
    last_four: z.string(),
  })
  .strict();

/** What registering a college returns, including its first code. */
export const tenantCreateResultSchema = z
  .object({
    tenant_id: z.string(),
    code: z.string(),
    name: z.string(),
    status: z.string(),
    activation: activationIssuanceSchema,
    onboarding: onboardingSummarySchema,
  })
  .strict();

/** What generating or regenerating an activation code returns. */
export const activationRotationResultSchema = z
  .object({
    tenant_id: z.string(),
    activation: activationIssuanceSchema,
    previous_code_revoked: z.boolean(),
  })
  .strict();

/** A college's onboarding detail, as the dedicated read returns it. */
export const collegeOnboardingViewSchema = z
  .object({
    tenant_id: z.string(),
    onboarding: onboardingSummarySchema,
    activation: activationSummarySchema,
  })
  .strict();

/** The amended registration the edit route returns. */
export const amendedCollegeSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    code: z.string(),
    address: z.string().default(''),
    status: tenantStatusV2Schema,
    registered_at: z.string(),
    token_issued_at: z.string().nullable().default(null),
    token_tail: z.string().nullable().default(null),
  })
  .strict();

// --- the validators are the contract, checked by the compiler ---------------
//
// Each line fails to compile if the schema above and the generated contract
// disagree. Written as assignments rather than as a test so the failure lands in
// `tsc`, where a drifted contract belongs — a runtime test would only fail on
// the one shape it happened to exercise.

const _activationSummaryMatches: Exact<
  z.infer<typeof activationSummarySchema>,
  components['schemas']['ActivationSummary']
> = true;

const _onboardingSummaryMatches: Exact<
  z.infer<typeof onboardingSummarySchema>,
  components['schemas']['OnboardingSummary']
> = true;

// `TenantOverview` and `TenantCreateResult` are checked one-directionally, and
// the reason is a deliberate strengthening rather than a gap. Platform declares
// `status` as an open `string`; this schema narrows it to the three values
// ADR-0067 defines, so the badge table below can be exhaustive and a fourth
// state becomes a compile error instead of a blank cell.
//
// Validating *more* strictly than the contract is safe: it refuses documents the
// contract permits but the product does not define. What would not be safe is
// inventing a field, so assignability to the contract type is still asserted —
// an added member or a renamed one still fails to compile.
const _tenantOverviewIsAContractRow: components['schemas']['TenantOverview'] =
  undefined as unknown as z.infer<typeof tenantOverviewSchema>;

const _tenantOverviewHasTheContractKeys: Exact<
  keyof z.infer<typeof tenantOverviewSchema>,
  keyof components['schemas']['TenantOverview']
> = true;

const _activationIssuanceMatches: Exact<
  z.infer<typeof activationIssuanceSchema>,
  components['schemas']['ActivationIssuance']
> = true;

const _tenantCreateResultIsAContractResult: components['schemas']['TenantCreateResult'] =
  undefined as unknown as z.infer<typeof tenantCreateResultSchema>;

const _tenantCreateResultHasTheContractKeys: Exact<
  keyof z.infer<typeof tenantCreateResultSchema>,
  keyof components['schemas']['TenantCreateResult']
> = true;

const _activationRotationResultMatches: Exact<
  z.infer<typeof activationRotationResultSchema>,
  components['schemas']['ActivationRotationResult']
> = true;

// Referenced so the checks are not elided as unused bindings.
export const CONTRACT_ASSERTIONS = [
  _activationSummaryMatches,
  _onboardingSummaryMatches,
  _tenantOverviewIsAContractRow,
  _tenantOverviewHasTheContractKeys,
  _activationIssuanceMatches,
  _tenantCreateResultIsAContractResult,
  _tenantCreateResultHasTheContractKeys,
  _activationRotationResultMatches,
] as const;

/** Validated activation state for one college. */
export type ActivationSummaryView = z.infer<typeof activationSummarySchema>;

/** Validated onboarding state for one college. */
export type OnboardingSummaryView = z.infer<typeof onboardingSummarySchema>;

/** One validated college row. */
export type TenantOverview = z.infer<typeof tenantOverviewSchema>;

/** A validated page of college rows. */
export type TenantOverviewPage = z.infer<typeof tenantOverviewPageSchema>;

/** A validated one-time disclosure. */
export type ActivationIssuance = z.infer<typeof activationIssuanceSchema>;

/** A validated registration result. */
export type TenantCreateResult = z.infer<typeof tenantCreateResultSchema>;

/** A validated rotation result. */
export type ActivationRotationResult = z.infer<typeof activationRotationResultSchema>;

/** A validated onboarding detail. */
export type CollegeOnboardingView = z.infer<typeof collegeOnboardingViewSchema>;

/** A validated amended registration. */
export type AmendedCollege = z.infer<typeof amendedCollegeSchema>;
